import { yupResolver } from '@hookform/resolvers/yup';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	Grid,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import React, { useState } from 'react';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import {
	AuthContext,
	AuthData,
	MULTI_CURRENCY_FEATURE_ENABLE,
	UI_MULTI_CURRENCY_FEATURE_ENABLE,
} from '../context/auth';
import { ConfigContext, ConfigData } from '../context/config';
import { CurrencyCode } from '../types/currencyCode';
import { Payment } from '../types/payments';
import { formatCurrency } from '../util/util';
import { StyledEmptyMessage } from './common';
import { FastForm } from './fastForm';
import { FORM_NAME } from './fastForm/data/constants';
import {
	getDefaultPolicyValues,
	getPaymentForm,
} from './fastForm/formBuilders';
import { PaymentFormValues } from './fastForm/formBuilders/getPaymentForm';
import { getValidationSchemaWithoutResolver } from './fastForm/utils/getValidationSchema';
import DefaultDatePicker from './form/DefaultDatePicker';
import { paymentSchema } from './payments/lib/paymentSchema';

interface Props {
	authData: AuthData;
	configData: ConfigData;
	payments: Payment[];
}

const PaymentDetails = (props: Props) => {
	const { authData, configData, payments } = props;
	const {
		paymentConfiguration: { paymentConfiguration },
	} = configData;
	const timezone = authData.auth?.timezone;
	const multiCurrencyEnabled =
		authData?.auth?.configProperties?.[MULTI_CURRENCY_FEATURE_ENABLE] &&
		authData?.auth?.configProperties?.[UI_MULTI_CURRENCY_FEATURE_ENABLE];

	const [expanded, setExpanded] = useState([] as string[]);

	const handleChange = (panel: any) => (event: any, isExpanded: boolean) => {
		const expandedAccordions = [...expanded];
		if (isExpanded) {
			expandedAccordions.push(panel);
		} else {
			const i = expanded.indexOf(panel);
			expandedAccordions.splice(i, 1);
		}
		setExpanded(expandedAccordions);
	};

	if (!payments.length) {
		return null;
	}
	const payment = payments[0]; // There will only ever be 1 payment
	const paymentFields = paymentConfiguration.fields;

	const form = getPaymentForm(
		{
			payment: payment,
			fields: paymentFields,
			fieldValues: payment?.fieldValues || {},
			mediaByLocator: {},
			timezone,
			multiCurrencyEnabled,
		},
		FORM_NAME.payment,
	);

	const paymentFormValues = getDefaultPolicyValues(form, authData);

	const specialFieldValues = {
		amountCurrency: payment?.amountCurrency || '',
		amount: payment?.amount || '',
		policyholderLocator: payment?.policyholderLocator || '',
		policyLocator: payment?.policyLocator || '',
	};

	const defaultValues = {
		payment: {
			...(paymentFormValues.payment as object),
			...specialFieldValues,
		},
	} as PaymentFormValues;

	const fallbackResolver: Resolver<any> = async () => ({
		values: {},
		errors: {},
	});

	if (!form) {
		return null;
	}

	const methods = useForm<PaymentFormValues>({
		defaultValues,
		mode: 'onBlur',
		reValidateMode: 'onChange',
		shouldFocusError: true,
		resolver:
			yupResolver(
				getValidationSchemaWithoutResolver(form).concat(paymentSchema(true)),
			) || fallbackResolver,
	});

	const noPaymentsMessage = () => {
		return <StyledEmptyMessage>No Payments</StyledEmptyMessage>;
	};

	return (
		<>
			<div style={{ margin: '16px 0' }}>Payments</div>
			{payments && payments.length > 0
				? payments.map((p: Payment) => {
						const title = (
							<Typography sx={{ width: '50%' }}>
								Payment {p.displayId}
								<Tooltip
									title='Copy payment locator to clipboard'
									arrow
									placement='top'
								>
									<IconButton
										color='info'
										aria-label='copy payment locator to clipboard'
										onClick={() => navigator.clipboard.writeText(p.locator)}
										size='small'
										sx={{ marginLeft: '6px', marginBottom: '2px' }}
									>
										<ContentCopyIcon sx={{ fontSize: 18 }} color='primary' />
									</IconButton>
								</Tooltip>
							</Typography>
						);
						const subTitle = p.reversedTimestamp ? (
							<Typography>Reversed</Typography>
						) : null;
						return (
							<FormProvider {...methods} key={p.locator}>
								<Accordion
									key={p.displayId}
									expanded={expanded.includes(p.displayId)}
									onChange={handleChange(p.displayId)}
								>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon />}
										id={p.displayId}
									>
										{title}
										{subTitle}
									</AccordionSummary>
									<AccordionDetails sx={{ pb: 2 }}>
										<Grid container spacing={2} sx={{ pb: 2 }}>
											<Grid item xs={4}>
												<DefaultDatePicker
													value={new Date(parseInt(p.postedTimestamp, 10))}
													onChange={null}
													label='Payment Date'
													style={{
														width: '100%',
														marginTop: '16px',
														marginRight: '4%',
													}}
													disabled
													showCalendar
													id={`${p.displayId}-payment-date`}
												/>
											</Grid>
											<Grid item xs={4}>
												<TextField
													id={`${p.displayId}-payment-amount`}
													sx={{ width: '100%', mt: '16px', mr: '4%' }}
													label='Payment Amount'
													value={formatCurrency(
														parseFloat(p.amount),
														p.amountCurrency as CurrencyCode,
													)}
													disabled
												/>
											</Grid>
											{p.reversedTimestamp ? (
												<Grid item xs={4}>
													<DefaultDatePicker
														value={new Date(parseInt(p.reversedTimestamp, 10))}
														onChange={null}
														label='Reversed Date'
														style={{ width: '100%', marginTop: '16px' }}
														disabled
														showCalendar
														id={`${p.displayId}-reversed-date`}
													/>
												</Grid>
											) : null}
										</Grid>
										<Grid container spacing={3}>
											<FastForm
												formName={FORM_NAME.payment}
												form={form}
												productName={null} // payments are separate from product config and therefore this is not needed
												configVersion='' // also not needed as payment config is not versioned
												authData={authData}
												disabled
											/>
										</Grid>
									</AccordionDetails>
								</Accordion>
							</FormProvider>
						);
				  })
				: noPaymentsMessage()}
		</>
	);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'configData' | 'authData'>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<ConfigContext.Consumer>
					{(configData) => (
						<Component {...props} authData={authData} configData={configData} />
					)}
				</ConfigContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(PaymentDetails);
